import { useTranslation } from 'react-i18next';

import { getTableCellErrorState } from '@/components/ui-v2/inputs/table/hooks/get-table-cell-error-state';
import { TablePriceDisplay } from '@/components/ui-v2/inputs/table/table-price-display';
import { TablePriceInput } from '@/components/ui-v2/inputs/table/table-price-input';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useIsCellEditing } from '@/hooks/use-is-cell-editing';
import { useIsOfferLocked } from '@/hooks/use-is-offer-locked';
import {
  DealItem,
  DealItemWithVersions,
} from '@/lib/graphql/generated/graphql';
import { cn, formatCurrency, getVatLabelKey } from '@/lib/utils';
import { rangeFilterFn } from '@/lib/utils/table-filter-utils';
import { useSetColumnSort } from '@/states/column-sort.state';
import { evolutionInsightsState } from '@/states/evolution-insights.state';
import { kpiComparisonState } from '@/states/kpi-comparison.state';
import { storeAtom } from '@/states/store.state';
import { KPIComparison } from '@/types/kpi-comparison';
import { Row } from '@tanstack/react-table';
import { useAtomValue } from 'jotai';
import { InfoIcon } from 'lucide-react';

import { ExtendedColumnDef } from '../table-products';
import { SortIcon } from './sort';
import { TableCellWithRecentlyModified } from './table-cell-with-recently-modified';

interface ColumnProps {
  handleCellClick: (id: string, field: keyof DealItem) => void;
  handleCellEdit: <K extends keyof DealItem>(
    id: string | undefined,
    field: K,
    value: DealItem[K],
    isLeafRow?: boolean
  ) => Promise<void>;
  handleKeyDown: React.KeyboardEventHandler<HTMLInputElement>;
  handleBlur: React.FocusEventHandler<HTMLInputElement>;
}

interface ListPriceCellContentProps {
  row: Row<DealItemWithVersions>;
  handleCellClick: ColumnProps['handleCellClick'];
  handleCellEdit: ColumnProps['handleCellEdit'];
  handleKeyDown: ColumnProps['handleKeyDown'];
  handleBlur: ColumnProps['handleBlur'];
}

function ListPriceCellContent({
  row,
  handleCellClick,
  handleCellEdit,
  handleKeyDown,
  handleBlur,
}: ListPriceCellContentProps) {
  const store = useAtomValue(storeAtom);
  const kpiComparison = useAtomValue(kpiComparisonState);
  const evolutionInsights = useAtomValue(evolutionInsightsState);
  const isOfferLocked = useIsOfferLocked();
  const isEditing = useIsCellEditing(row, 'listPrice');
  const isParent = row.subRows.length > 1;

  const getValue = () => {
    const value = row.original.activeVersion.listPrice?.toFixed(2);
    if (typeof value === 'undefined' || value === null) return '—';
    return formatCurrency(parseFloat(value), store);
  };

  const getParentValue = () => {
    const subRowsValues: number[] = [];
    row.subRows.forEach((subRow) => {
      const value = subRow.original.activeVersion.listPrice?.toFixed(2);
      if (typeof value === 'undefined' || value === null) return;
      subRowsValues.push(parseFloat(value));
    });
    if (subRowsValues.length === 0) return '—';
    const min = formatCurrency(Math.min(...subRowsValues), store);
    const max = formatCurrency(Math.max(...subRowsValues), store);
    return min === max ? max : `${min} - ${max}`;
  };

  const getParentComparedValue = () => {
    if (
      kpiComparison !== KPIComparison.DISCOUNT_HIGHER &&
      kpiComparison !== KPIComparison.DISCOUNT_LOWER
    ) {
      return '—';
    }
    const subRowsValues: number[] = [];
    row.subRows.forEach((subRow) => {
      const comparedItem = evolutionInsights?.campaignItemsWithEvolution.find(
        (item) => item.dealItemId === subRow.original.id
      );
      if (
        comparedItem?.listPrice !== undefined &&
        comparedItem.listPrice !== null
      ) {
        subRowsValues.push(comparedItem.listPrice / 100);
      }
    });
    if (subRowsValues.length === 0) return '—';
    const min = formatCurrency(Math.min(...subRowsValues), store);
    const max = formatCurrency(Math.max(...subRowsValues), store);
    return min === max ? max : `${min} - ${max}`;
  };

  const errorState = getTableCellErrorState({
    row,
    field: 'listPrice',
    errorType: 'empty',
  });

  const getPreviousItem = () => {
    if (kpiComparison === KPIComparison.DISCOUNT_HIGHER) {
      return evolutionInsights?.campaignItemsWithEvolution.find(
        (item) => item.dealItemId === row.original.id
      )?.listPrice;
    }
    if (kpiComparison === KPIComparison.DISCOUNT_LOWER) {
      return evolutionInsights?.campaignItemsWithEvolution.find(
        (item) => item.dealItemId === row.original.id
      )?.listPrice;
    }
  };

  const hasComparison =
    kpiComparison === KPIComparison.DISCOUNT_HIGHER ||
    kpiComparison === KPIComparison.DISCOUNT_LOWER;

  return (
    <TableCellWithRecentlyModified
      productId={row.original.id}
      field="listPrice"
      isParent={isParent}
    >
      {(isRecentlyModified) => (
        <div className="flex h-full w-full flex-row">
          <div className={cn('h-full', hasComparison ? 'w-[160px]' : 'w-full')}>
            {isEditing ? (
              <TablePriceInput
                value={row.original.activeVersion.listPrice ?? null}
                onChange={async (value) => {
                  await handleCellEdit(
                    row.original.activeVersion.id,
                    'listPrice',
                    value,
                    !isParent
                  );
                }}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
                disabled={isOfferLocked}
                autoFocus={isEditing}
                hasError={errorState.hasError}
              />
            ) : (
              <TablePriceDisplay
                value={
                  errorState.isEmpty
                    ? null
                    : isParent
                      ? getParentValue()
                      : getValue()
                }
                isEmpty={errorState.isEmpty}
                hasError={errorState.hasError}
                errorMessage={errorState.errorMessage}
                isEditable={!isOfferLocked}
                isParent={isParent}
                isRecentlyModified={isRecentlyModified}
                onClick={() =>
                  !isParent &&
                  handleCellClick(row.original.activeVersion.id, 'listPrice')
                }
              />
            )}
          </div>
          {hasComparison && (
            <div
              className={cn(
                'flex-1 text-right',
                (isParent || isOfferLocked) && 'opacity-50',
                isParent || !isOfferLocked
                  ? 'cursor-pointer'
                  : 'cursor-not-allowed'
              )}
              onClick={() =>
                !isParent &&
                handleCellClick(row.original.activeVersion.id, 'listPrice')
              }
            >
              <div
                className={`flex h-full w-full items-center justify-end px-2 text-sm`}
              >
                <span>
                  {!isParent
                    ? formatCurrency((getPreviousItem() ?? 0) / 100, store)
                    : getParentComparedValue()}
                </span>
              </div>
            </div>
          )}
        </div>
      )}
    </TableCellWithRecentlyModified>
  );
}

const useColumnDefListPrice = ({
  handleCellClick,
  handleCellEdit,
  handleKeyDown,
  handleBlur,
}: ColumnProps): ExtendedColumnDef<DealItemWithVersions> => {
  const { t } = useTranslation();
  const store = useAtomValue(storeAtom);
  const kpiComparison = useAtomValue(kpiComparisonState);
  const { setColumnSort } = useSetColumnSort();

  const accessorKey: keyof DealItem = 'listPrice';

  return {
    accessorKey: accessorKey,
    name: 'listPrice',
    filterFn: rangeFilterFn,
    accessorFn: (row) => row.activeVersion?.listPrice ?? 0,
    aggregationFn: 'mean',
    sortingFn: 'basic',
    header: () => (
      <div className="flex w-full cursor-pointer flex-row font-semibold">
        <div className="flex w-[160px] items-center gap-2">
          <Tooltip>
            <TooltipTrigger asChild>
              <div>
                <InfoIcon className="h-4 w-4" />
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p>{t('kpi.list_price_tooltip')}</p>
            </TooltipContent>
          </Tooltip>{' '}
          <div
            className="flex flex-1 items-center gap-2"
            onClick={() => setColumnSort(accessorKey)}
          >
            {t('kpi.list_price')} ({t(getVatLabelKey(store))})
            <SortIcon pointer="listPrice" />
          </div>
        </div>
        {(kpiComparison === KPIComparison.DISCOUNT_HIGHER ||
          kpiComparison === KPIComparison.DISCOUNT_LOWER) && (
          <div
            className="flex w-full flex-1 items-center justify-end gap-2 text-right"
            onClick={() => setColumnSort(accessorKey)}
          >
            {t('kpi.list_price_compared')}
            <SortIcon pointer="listPrice" />
          </div>
        )}
      </div>
    ),
    cell: ({ row }) => (
      <ListPriceCellContent
        row={row}
        handleCellClick={handleCellClick}
        handleCellEdit={handleCellEdit}
        handleKeyDown={handleKeyDown}
        handleBlur={handleBlur}
      />
    ),
    size:
      kpiComparison === KPIComparison.DISCOUNT_HIGHER ||
      kpiComparison === KPIComparison.DISCOUNT_LOWER
        ? 340
        : 180,
  };
};

export default useColumnDefListPrice;
